<template>
    <div />
</template>

<script>
import { mapState } from 'vuex';
import CourseSettings from '@/models/CourseSettings.js';
import axios from '@/plugins/axios.js';

export default {
    name: 'CourseKeyIntegrationListener',

    data() {
        return {
            pollingInterval: null,
            pollingStartTime: null,
            maxPollingDuration: 5 * 60 * 1000, // 5 minutes in milliseconds
        };
    },

    computed: {
        ...mapState('Auth', { authUser: state => state.user }),
        ...mapState('CourseTemplateForm', ['form']),

        courseId() {
            return this.form?.course?.id;
        },

        isEditingCourse() {
            return this.courseId && this.$route.name === 'course-edit-settings';
        },
    },

    watch: {
        courseId: {
            immediate: true,
            handler(newCourseId, oldCourseId) {
                if (oldCourseId && oldCourseId !== newCourseId) {
                    this.removeListener(oldCourseId);
                    this.stopPolling();
                }

                if (newCourseId && this.isEditingCourse) {
                    this.setupListener(newCourseId);
                }
            },
        },
    },

    beforeUnmount() {
        if (this.courseId) {
            this.removeListener(this.courseId);
        }
        this.stopPolling();
    },

    methods: {
        setupListener(courseId) {
            this.$echo
                .private(`course-key-integration-${courseId}`)
                .listen('CourseKeyIntegrationStarted', this.handleStarted)
                .listen('CourseKeyIntegrationCompleted', this.handleCompleted)
                .listen('CourseKeyIntegrationFailed', this.handleFailed)
                .error(error => {
                    console.error('CourseKey integration websocket error:', error);
                });
        },

        removeListener(courseId) {
            const channel = this.$echo.private(`course-key-integration-${courseId}`);
            channel.stopListening('CourseKeyIntegrationStarted');
            channel.stopListening('CourseKeyIntegrationCompleted');
            channel.stopListening('CourseKeyIntegrationFailed');
        },

        handleStarted(payload) {
            this.$store.commit('CourseTemplateForm/SET_COURSE_KEY_INTEGRATION_IN_PROGRESS', true);

            // Keep autosaver in "Saving..." state
            this.$store.commit('AutoSave/SET_SAVING', {
                indicator: 'CourseTemplateForm/courseKeyIntegration',
                message: 'Saving...',
            });

            // Start polling fallback in case websocket fails
            this.startPolling();
        },

        handleCompleted(payload) {
            this.stopPolling();
            this.$store.commit('CourseTemplateForm/SET_COURSE_KEY_INTEGRATION_IN_PROGRESS', false);
            this.$store.commit('CourseTemplateForm/SET_COURSE_KEY_INTEGRATION_ERROR', null);

            // Mark autosaver as saved
            this.$store.commit('AutoSave/SET_SAVED', {
                indicator: 'CourseTemplateForm/courseKeyIntegration',
                message: 'Saved',
            });

            this.$edverumToast.open({
                message: 'CourseKey integration completed successfully',
                type: 'is-success',
                duration: 5000,
            });
        },

        handleFailed(payload) {
            this.stopPolling();
            this.$store.commit('CourseTemplateForm/SET_COURSE_KEY_INTEGRATION_IN_PROGRESS', false);
            this.$store.commit('CourseTemplateForm/SET_COURSE_KEY_INTEGRATION_ERROR', payload.error);

            // Mark autosaver as saved (to clear the "Saving..." state)
            this.$store.commit('AutoSave/SET_SAVED', {
                indicator: 'CourseTemplateForm/courseKeyIntegration',
                message: 'Saved',
            });

            // Revert the course_key_enabled setting to false
            const courseSettings = this.form.course.course_settings;
            if (courseSettings?.id) {
                CourseSettings.find(courseSettings.id).$update({ course_key_enabled: false });

                this.$store.commit('CourseTemplateForm/updateCourseField', {
                    field: 'course_settings.course_key_enabled',
                    value: false,
                });
            }

            this.$edverumToast.open({
                message: `CourseKey integration failed: ${payload.error}`,
                type: 'is-danger',
                duration: 10000,
            });
        },

        startPolling() {
            // Clear any existing polling interval
            this.stopPolling();

            // Set start time for timeout tracking
            this.pollingStartTime = Date.now();

            // Start polling every 30 seconds
            this.pollingInterval = setInterval(() => {
                this.checkIntegrationStatus();
            }, 30000); // 30 seconds

            // Also check immediately after a short delay to catch quick completions
            setTimeout(() => {
                this.checkIntegrationStatus();
            }, 2000);
        },

        stopPolling() {
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
            }
            this.pollingStartTime = null;
        },

        async checkIntegrationStatus() {
            // Stop polling if max duration exceeded
            if (this.pollingStartTime && Date.now() - this.pollingStartTime > this.maxPollingDuration) {
                this.stopPolling();
                console.warn('CourseKey integration polling timeout reached. Please refresh the page to check status.');
                return;
            }

            // Only poll if integration is still in progress
            if (!this.$store.state.CourseTemplateForm.courseKeyIntegrationInProgress) {
                this.stopPolling();
                return;
            }

            if (!this.courseId) {
                return;
            }

            try {
                const { data } = await axios.get(`/api/courses/${this.courseId}?basic-information`, {
                    errorHandle: false,
                });

                const course = data?.data;

                // Check if integration completed successfully
                // Integration is complete when both course_key_id and course_key_add_code are set
                if (course?.course_key_id && course?.course_key_add_code) {
                    this.handleCompleted({
                        course_id: this.courseId,
                        model_type: 'course',
                        model_id: this.courseId,
                        status: 'completed',
                    });
                }
            } catch (error) {
                // Silently handle errors - don't spam console if API fails
                // We'll retry on next polling interval
                console.warn('CourseKey integration status check failed:', error);
            }
        },
    },
};
</script>
